/**
 * Birthday PDP — scroll reveal
 *
 * Marks each child row visible as it scrolls into view, so the timeline lifts
 * in one step at a time. Rows are observed individually rather than the whole
 * section, which is what produces the stagger.
 *
 * The resting state in CSS is the revealed one — the hidden state only applies
 * inside a prefers-reduced-motion: no-preference block. If this script never
 * runs, the content still reads correctly.
 */
class BirthdayReveal extends HTMLElement {
  connectedCallback() {
    this.rows = [...this.querySelectorAll('[data-bd-row]')];
    if (!this.rows.length) return;

    if (!('IntersectionObserver' in window)) {
      for (const row of this.rows) row.dataset.visible = 'true';
      return;
    }

    const replay = this.dataset.replay === 'true';

    this.observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.intersectionRatio >= 0.25) {
            entry.target.dataset.visible = 'true';
            if (!replay) this.observer.unobserve(entry.target);
          } else if (replay && entry.intersectionRatio === 0) {
            // Reset only when fully off screen, so nobody sees it undo.
            entry.target.dataset.visible = 'false';
          }
        }
      },
      { threshold: [0, 0.25] }
    );

    for (const row of this.rows) this.observer.observe(row);
  }

  disconnectedCallback() {
    this.observer?.disconnect();
  }
}

if (!customElements.get('birthday-reveal')) {
  customElements.define('birthday-reveal', BirthdayReveal);
}
